import { Button } from '@forwardflow/ui'
import Icon from '@forwardflow/ui/Icons'
import { Modal, Skeleton } from 'antd'
import { useEffect, useState } from 'react'
import { AgentsWithMostContracts } from '../LeadDetailsModal/AgentsWithMostContracts'
import { WorkingHours } from '../LeadDetailsModal/WorkingHours'
import { useActiveLeadDetailsModalStore } from '@forwardflow/store/modal/ActiveLeadDetailsModal'
import AutoNurtureImpact from './AutoNurtureImpact'
import TopAgentsPersonas from './TopAgentsPersonas'
import { RecentConversations } from './RecentConversations'
import { useNavigate } from 'react-router-dom'
import CampaignService from '@forwardflow/services/CampaignService'
import {
   useCampaignDetailsByLeadSource,
   useGetActiveLeadSource,
} from '@forwardflow/features/campaign'
import { formatNumber } from '@forwardflow/helpers/utils/formatNumber'
import { ContractIcon } from '@forwardflow/assets/svg/ContractIcon'
import { LeadsNurturedIcon } from '@forwardflow/assets/svg/LeadsNurturedIcon'

interface ActiveLeadDetailsModalProps {
   onStatusChange?: (id: string, status: string) => void
}

export const ActiveLeadDetailsModal = ({
   onStatusChange,
}: ActiveLeadDetailsModalProps) => {
   const [isCampaignActive, setIsCampaignActive] = useState(true)

   const [isStatusChanging, setIsStatusChanging] = useState(false)
   const {
      isActiveLeadDetailsModalOpen,
      closeActiveLeadDetailsModal,
      campaignName,
      campaignId,
      statType,
   } = useActiveLeadDetailsModalStore()

   const navigate = useNavigate()
   const navigateToEditCampaign = () => {
      navigate(
         `/campaign/${encodeURIComponent(
            campaignId as string
         )}/phone-number?campaignName=${campaignName}`
      )
      closeActiveLeadDetailsModal()
   }

   const { data: activateLeadSourceDetails, isLoading } =
      useCampaignDetailsByLeadSource(campaignId || '', statType)

   const { data: activeLeadSourceDetails, mutate } = useGetActiveLeadSource(
      campaignId || ''
   )

   useEffect(() => {
      if (activeLeadSourceDetails) {
         setIsCampaignActive(activeLeadSourceDetails.status === 'active')
      }
   }, [activeLeadSourceDetails])

   const onCampaignStatusChange = async () => {
      setIsStatusChanging(true)
      try {
         await CampaignService.updateCampaignStatus(
            campaignId as string,
            isCampaignActive ? 'paused' : 'active'
         )
         mutate()
         if (campaignId) {
            onStatusChange?.(campaignId, isCampaignActive ? 'paused' : 'active')
         }
      } catch (error) {
         console.log(error)
      } finally {
         setIsStatusChanging(false)
      }
   }

   return (
      <>
         <Modal
            className={`ff-modal !w-[800px]  my-4 ${!isLoading ? 'p-0' : ''}`}
            title=""
            open={isActiveLeadDetailsModalOpen}
            onCancel={closeActiveLeadDetailsModal}
            footer={null}
            closeIcon={false}
            centered
         >
            {!isLoading ? (
               <>
                  <div>
                     <div className="flex items-center justify-between py-3 border-b border-white/20 px-11">
                        <p className="text-[20px] font-medium">
                           {campaignName}
                        </p>
                        <div className="flexc gap-x-4">
                           <Button
                              onClick={navigateToEditCampaign}
                              className="!w-[155px] flexc gap-x-2 !mt-0 !bg-black/30 hover:!bg-black/50 !border-none"
                           >
                              <Icon name="Edit" className="-mt-1" />
                              <p>Edit</p>
                           </Button>

                           {!(activeLeadSourceDetails?.status === 'draft') ? (
                              <Button
                                 isLoading={isStatusChanging}
                                 onClick={onCampaignStatusChange}
                                 className="!w-[155px] flexc gap-x-2 !mt-0 !bg-black/30 hover:!bg-black/50 !border-none"
                              >
                                 {isCampaignActive ? (
                                    <>
                                       <Icon
                                          name="Pause"
                                          className="-mt-1 fill-[#D53E59]"
                                       />
                                       <p className="text-[#D53E59]">Pause</p>
                                    </>
                                 ) : (
                                    <>
                                       <Icon
                                          name="Play"
                                          className="-mt-1 scale-75"
                                       />
                                       <p>Resume</p>
                                    </>
                                 )}
                              </Button>
                           ) : (
                              ''
                           )}
                           <div
                              onClick={closeActiveLeadDetailsModal}
                              className="cursor-pointer group"
                           >
                              <Icon
                                 name="Close"
                                 className="fill-[#818181] group-hover:fill-[#818181]/80"
                              />
                           </div>
                        </div>
                     </div>

                     <div className="mt-5 px-11">
                        <div className="mb-4">
                           {(activateLeadSourceDetails?.campaignStats
                              ?.topMetrics?.current.totalLeads ||
                              activateLeadSourceDetails?.campaignStats
                                 ?.topMetrics?.current.totalAppointments !=
                                 null ||
                              activateLeadSourceDetails?.campaignStats
                                 ?.topMetrics?.current.totalContracts !=
                                 null) && (
                              <div className="relative flex overflow-hidden rounded h-52 bg-primary/30">
                                 {activateLeadSourceDetails?.campaignStats
                                    ?.topMetrics?.current.totalLeads && (
                                    <div className="flex-col flex-1 py-5 text-center border-r flexc border-white/20 px-7">
                                       <div className="h-[38px] w-[38px] flexc shrink-0 mb-5">
                                          <LeadsNurturedIcon
                                             className="mx-auto"
                                             width={38}
                                             height={38}
                                          />
                                       </div>
                                       <p className="text-white/70 text-[14px] font-medium mb-1">
                                          New leads
                                       </p>
                                       <p className="text-[32px] font-medium">
                                          {formatNumber(
                                             activateLeadSourceDetails
                                                ?.campaignStats?.topMetrics
                                                ?.current.totalLeads
                                          )}
                                       </p>
                                    </div>
                                 )}
                                 {activateLeadSourceDetails?.campaignStats
                                    ?.topMetrics?.current.totalAppointments !=
                                    null && (
                                    <div className="flex-col flex-1 py-5 text-center border-r flexc border-white/20 px-7">
                                       <div className="h-[38px] w-[38px] mb-5 flexc shrink-0 ">
                                          <Icon
                                             name="Calendar"
                                             className="mx-auto fill-[#AB7AE0] scale-[1.9]"
                                          />
                                       </div>

                                       <p className="text-white/70 text-[14px] font-medium mb-1">
                                          Appointments
                                       </p>
                                       <p className="text-[32px] font-medium">
                                          {formatNumber(
                                             activateLeadSourceDetails
                                                ?.campaignStats?.topMetrics
                                                ?.current.totalAppointments
                                          )}
                                       </p>
                                    </div>
                                 )}
                                 {activateLeadSourceDetails?.campaignStats
                                    ?.topMetrics?.current.totalContracts !=
                                    null && (
                                    <div className="flex-col flex-1 py-5 text-center px-7 flexc">
                                       <div className="h-[38px] w-[38px] flexc shrink-0 mb-5">
                                          <ContractIcon
                                             className="mx-auto"
                                             width={38}
                                             height={38}
                                          />
                                       </div>
                                       <p className="text-white/70 text-[14px] font-medium mb-1">
                                          Contracts
                                       </p>
                                       <p className="text-[32px] font-medium">
                                          {formatNumber(
                                             activateLeadSourceDetails
                                                ?.campaignStats?.topMetrics
                                                ?.current.totalContracts
                                          )}
                                       </p>
                                    </div>
                                 )}
                              </div>
                           )}
                        </div>
                        <AutoNurtureImpact
                           analytics={activateLeadSourceDetails?.campaignStats}
                        />
                        <div className="flex mb-4 gap-x-3">
                           {activateLeadSourceDetails?.topAgentPersonas &&
                              activateLeadSourceDetails.topAgentPersonas
                                 .length > 0 && (
                                 <div className="flex-1">
                                    <TopAgentsPersonas
                                       topAgentPersonas={
                                          activateLeadSourceDetails.topAgentPersonas
                                       }
                                    />
                                 </div>
                              )}

                           <div className="flex-1">
                              <WorkingHours
                                 bestTime={
                                    activateLeadSourceDetails?.bestTimeRange
                                 }
                                 callSummary={
                                    activateLeadSourceDetails?.callSummary
                                 }
                              />
                           </div>
                        </div>
                        <div className="flex flex-col flex-wrap gap-4 pb-5">
                           {activateLeadSourceDetails?.recentConversations &&
                              activateLeadSourceDetails.recentConversations
                                 .length > 0 && (
                                 <div className="flex-1">
                                    <RecentConversations
                                       recentConversations={
                                          activateLeadSourceDetails.recentConversations
                                       }
                                    />
                                 </div>
                              )}
                           {activateLeadSourceDetails?.agentsWithMostContracts &&
                              activateLeadSourceDetails.agentsWithMostContracts
                                 .length > 0 && (
                                 <div className="flex-1">
                                    <AgentsWithMostContracts
                                       agentsWithMostContracts={
                                          activateLeadSourceDetails.agentsWithMostContracts
                                       }
                                       direction="row"
                                       count={3}
                                    />
                                 </div>
                              )}
                        </div>
                     </div>
                  </div>
               </>
            ) : (
               <div className="flexc">
                  <Skeleton active />
               </div>
            )}
         </Modal>
      </>
   )
}
